import UserDetails from '../models/UserDetails';
import { UserRoles } from '../models/UserRole';
import { UserTypes } from 'src/models/UserType';
import { isArray } from 'util';
import * as _ from 'lodash';

export default class Authorization {
    public static ShowElement(nav: JSX.Element, roles: UserRoles | Array<UserRoles>, types: UserTypes | Array<UserTypes>, user: UserDetails) {
        const inRole = roles ? (Authorization.IsInRole(roles, user) ? true : false) : true;
        const inType = types ? (Authorization.IsInType(types, user) ? true : false) : true;

        return inRole && inType ? nav : undefined;
    }

    public static IsInRole(roles: UserRoles | Array<UserRoles>, user: UserDetails) {
        if (isArray(roles)) {
            return _.includes(roles, user.role);
        } else {
            return user.role === roles;
        }
    }

    public static HasRole(role: string, user: UserDetails) {
        return !!user && Authorization.ContainsClaimValue(user.roles, role);
    }

    public static HasAnyRole(roles: string[], user: UserDetails) {
        return !!roles && roles.some((role) => Authorization.HasRole(role, user));
    }

    public static HasPermission(permission: string, user: UserDetails) {
        return (
            !!user &&
            (Authorization.IsInRole(UserRoles.Admin, user) ||
                Authorization.HasRole('Admin', user) ||
                Authorization.ContainsClaimValue(user.permissions, permission))
        );
    }

    public static HasAnyPermission(permissions: string[], user: UserDetails) {
        return !!permissions && permissions.some((permission) => Authorization.HasPermission(permission, user));
    }

    public static JwtRoles(roles: string | string[]) {
        return Authorization.NormalizeClaimValues(roles);
    }

    public static JwtPermissions(permissions: string | string[]) {
        return Authorization.NormalizeClaimValues(permissions);
    }

    public static IsInType(types: UserTypes | Array<UserTypes>, user: UserDetails) {
        if (isArray(types)) {
            return _.includes(types, user.type);
        } else {
            return user.type === types;
        }
    }

    public static IsStaffed(tabName: string, projectId: number, user: UserDetails) {
        switch (tabName) {
            case 'Owners':
                let ownerProjects: string[] = user.ownershipAccess ? user.ownershipAccess.split(',') : [];
                if (ownerProjects.indexOf(ownerProjects.find(op => op === projectId?.toString())) > -1) {
                    return true;
                }
                return false;
            case 'Contracts':
                let contractProjects: string[] = user.contractAccess ? user.contractAccess.split(',') : [];
                if (contractProjects.indexOf(contractProjects.find(op => op === projectId?.toString())) > -1) {
                    return true;
                }
                return false;
            case 'Reports':
                let reportProjects: string[] = user.reportAccess ? user.reportAccess.split(',') : [];
                if (reportProjects.indexOf(reportProjects.find(op => op === projectId?.toString())) > -1) {
                    return true;
                }
                return false;
            case 'Mapping':
                return true;
            case 'Staffing':
            case 'Settings':
                return false;
            default:
                return false;
        }
    }

    public static JwtUserRole(role: string) {
        let userRole: UserRoles = undefined;

        switch (role) {
            case 'ad':
                userRole = UserRoles.Admin;
                break;
            case 'am':
                userRole = UserRoles.AccountingAdmin;
                break;
            case 'pm':
                userRole = UserRoles.ProspectManager;
                break;
            case 'pc':
                userRole = UserRoles.ProjectCoordinator;
                break;
            case 'ha':
                userRole = UserRoles.HRAdmin;
                break;
            case 'hr':
                userRole = UserRoles.HR;
                break;
            case 'aa':
                userRole = UserRoles.AccountingAdmin;
                break;
            case 'ac':
                userRole = UserRoles.Accounting;
                break;
            case 'lm':
                userRole = UserRoles.Landman;
                break;
            default:
                role = undefined;
        }

        return userRole;
    }

    public static JwtUserType(type: string) {
        let userType: UserTypes = undefined;

        switch (type) {
            case 'em':
                userType = UserTypes.Employee;
                break;
            case 'lm':
                userType = UserTypes.Landman;
                break;
            case 'cl':
                userType = UserTypes.ClientLandman;
                break;
            default:
                userType = undefined;
                break;
        }

        return userType;
    }

    private static NormalizeClaimValues(value: string | string[]) {
        const values = Array.isArray(value) ? value : value ? [value] : [];

        return values.reduce((normalized: string[], item: string) => {
            const trimmed = typeof item === 'string' ? item.trim() : '';
            if (trimmed && !Authorization.ContainsClaimValue(normalized, trimmed)) {
                normalized.push(trimmed);
            }
            return normalized;
        }, []);
    }

    private static ContainsClaimValue(values: string[], expected: string) {
        if (!values || !expected) {
            return false;
        }

        return values.some((value) => value.toLowerCase() === expected.toLowerCase());
    }
}



// WEBPACK FOOTER //
// ./src/util/Authorization.ts